import React from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import { fmtMoney } from '../helpers';
import type { BankAccount } from '../types';

type Props = {
  accounts: ReadonlyArray<BankAccount>;
  totalBalance: number;
  lastUpdatedLabel?: string;
  onGoToRegisters?: () => void;
  onHelpful?: (helpful: boolean) => void;
};

const Thumb = ({ down, onClick }: { down?: boolean; onClick?: () => void }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        width: 26, height: 26, borderRadius: '50%',
        border: '1px solid ' + (hovered ? C.teal : C.line),
        background: hovered ? C.teal : '#fff',
        display: 'grid', placeItems: 'center', cursor: 'pointer',
      }}
    >
      <svg viewBox="0 0 24 24" width={13} height={13} fill="none"
        stroke={hovered ? '#fff' : C.sub} strokeWidth={2.2}
        style={down ? { transform: 'scaleY(-1)' } : undefined}>
        <path d="M7 10v12H3V10z" />
        <path d="M7 10l5-8c2 0 3 1 3 3v3h5a2 2 0 012 2l-2 10a2 2 0 01-2 2H7" />
      </svg>
    </span>
  );
};

function BankIcon({ kind }: { kind: BankAccount['kind'] }) {
  const common = {
    width: 22, height: 22, fill: 'none', stroke: '#fff', strokeWidth: 2.6,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  if (kind === 'savings') {
    // Dollar in circle (Option A)
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <circle cx="12" cy="12" r="9"/>
        <path d="M15 8h-4a2 2 0 100 4h2a2 2 0 110 4h-5"/>
        <line x1="12" y1="6" x2="12" y2="8"/>
        <line x1="12" y1="16" x2="12" y2="18"/>
      </svg>
    );
  }
  // Chequing: bank building (pillars + roof)
  return (
    <svg viewBox="0 0 24 24" {...common}>
      <path d="M3 10 L12 3 L21 10"/>
      <line x1="2" y1="21" x2="22" y2="21"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <line x1="6" y1="10" x2="6" y2="19"/>
      <line x1="10" y1="10" x2="10" y2="19"/>
      <line x1="14" y1="10" x2="14" y2="19"/>
      <line x1="18" y1="10" x2="18" y2="19"/>
    </svg>
  );
}

export default function BankAccountsCard({
  accounts,
  totalBalance,
  lastUpdatedLabel = 'Just updated',
  onGoToRegisters,
  onHelpful,
}: Props) {
  return (
    <div style={{
      background: C.card,
      border: '1px solid ' + C.line,
      borderRadius: 14,
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: F.family,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.sub, textTransform: 'uppercase' }}>
          Bank Accounts
        </span>
        <span style={{ fontSize: 12, color: C.sub }}>As of today</span>
      </div>

      <p style={{ fontSize: 12.5, color: C.sub, margin: '0 0 6px' }}>Total bank balance</p>
      <div style={{
        fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: '-0.015em',
        fontVariantNumeric: 'tabular-nums', marginBottom: 4,
      }}>
        {fmtMoney(totalBalance)}
      </div>
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 14 }}>
        {lastUpdatedLabel} · {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
      </div>

      {accounts.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.sub, padding: '18px 0', fontStyle: 'italic' }}>
          No bank accounts connected yet.
        </div>
      ) : (
        <div style={{ fontSize: 12.5 }}>
          {accounts.map((acct, i) => (
            <div key={acct.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px 0',
              borderTop: i === 0 ? '0' : '1px solid ' + C.lineSoft,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', background: C.teal,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <BankIcon kind={acct.kind} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: C.ink, marginBottom: 2 }}>{acct.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 10px', fontSize: 11.5, color: C.sub }}>
                  <span>Bank balance</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: C.ink, fontWeight: 600 }}>{fmtMoney(acct.bankBalance)}</span>
                  {acct.inNovalaBalance !== null && (
                    <>
                      <span>In Novala</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: C.ink, fontWeight: 600 }}>{fmtMoney(acct.inNovalaBalance)}</span>
                    </>
                  )}
                </div>
                {acct.needsAttention && (
                  <div style={{ fontSize: 11, color: C.amber, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke={C.amber} strokeWidth={2.4}>
                      <circle cx="12" cy="12" r="9" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12" y2="16.5" />
                    </svg>
                    Needs attention
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span onClick={onGoToRegisters} style={{ fontSize: 12.5, color: C.tealInk, fontWeight: 700, cursor: 'pointer' }}>
          Go to registers →
        </span>
        <span style={{ fontSize: 11, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Helpful?
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Thumb onClick={() => onHelpful?.(true)} />
            <Thumb down onClick={() => onHelpful?.(false)} />
          </span>
        </span>
      </div>
    </div>
  );
}
